import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  CalendarDays, ArrowLeft, Edit, Save, X, Plus, Trash2, Check, Clock,
  MessageCircle, Mail, MapPin, Video, Phone, Users, FileText, ClipboardList,
  Copy, AlertCircle, CheckSquare, Paperclip, Upload, FileDown, RefreshCw
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/helpers';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import { buildWaUrl } from '../utils/whatsapp';
import { downloadExport } from '../utils/exportUtils';

// ── Constants ─────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'details',   label: 'Details',       icon: CalendarDays },
  { id: 'agenda',    label: 'Agenda & Notes', icon: FileText },
  { id: 'actions',   label: 'Action Items',   icon: ClipboardList },
  { id: 'mom',       label: 'MOM',            icon: FileText },
  { id: 'documents', label: 'Documents',      icon: Paperclip },
];

const STATUSES = ['Scheduled', 'In Progress', 'Completed', 'Cancelled', 'Postponed'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const ACTION_STATUSES = ['Pending', 'In Progress', 'Done'];

const STATUS_COLOR = {
  'Scheduled':   'bg-blue-100 text-blue-700',
  'In Progress': 'bg-indigo-100 text-indigo-700',
  'Completed':   'bg-green-100 text-green-700',
  'Cancelled':   'bg-gray-100 text-gray-500',
  'Postponed':   'bg-orange-100 text-orange-700',
};

const PRIORITY_COLOR = {
  'Low':    'text-gray-500 bg-gray-50',
  'Medium': 'text-blue-600 bg-blue-50',
  'High':   'text-orange-600 bg-orange-50',
  'Urgent': 'text-red-600 bg-red-50',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt12(time) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function fmtBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(name) {
  const ext = name?.split('.').pop()?.toLowerCase();
  if (['pdf'].includes(ext)) return '📄';
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️';
  if (['zip', 'rar', '7z'].includes(ext)) return '🗜️';
  return '📎';
}

// ── MOM Generator ─────────────────────────────────────────────────────────────
function generateMOMText(meeting) {
  const date = fmtDate(meeting.meeting_date);
  const time = fmt12(meeting.meeting_time);
  const dur = meeting.duration_minutes ? `${meeting.duration_minutes} minutes` : '';

  const internalAttendees = (meeting.attendees || []).filter(a => a.attendee_type === 'Internal');
  const externalAttendees = (meeting.attendees || []).filter(a => a.attendee_type === 'External');

  const internalNames = internalAttendees.map(a => a.user_name || 'Staff').join(', ') || '—';
  const externalNames = externalAttendees.map(a => `${a.external_name}${a.external_mobile ? ` (${a.external_mobile})` : ''}`).join(', ') || '—';

  const agendaLines = meeting.agenda
    ? meeting.agenda.split('\n').map((l, i) => `  ${i + 1}. ${l}`).join('\n')
    : '  —';

  const notesLines = meeting.notes
    ? meeting.notes.split('\n').map(l => `  ${l}`).join('\n')
    : '  —';

  const pendingActions = (meeting.action_items || []).filter(a => a.status !== 'Done');
  const actionLines = pendingActions.length
    ? pendingActions.map((a, i) => `  ${i + 1}. ${a.action_item}\n     Assigned To : ${a.assignee_name || 'TBD'}\n     Due Date    : ${a.due_date ? fmtDate(a.due_date) : 'TBD'}\n     Priority    : ${a.priority}`).join('\n\n')
    : '  No pending action items.';

  const preparedBy = meeting.created_by_name || meeting.assigned_to_name || 'KU Associates Team';
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });

  return `══════════════════════════════════════════════
MINUTES OF MEETING
Krishna Uday & Associates
Chartered Accountants, Hyderabad
══════════════════════════════════════════════

Meeting  : ${meeting.title}
Date     : ${date}
Time     : ${time}${dur ? `  (Duration: ${dur})` : ''}
Mode     : ${meeting.mode}${meeting.location ? `\nVenue    : ${meeting.location}` : ''}
Client   : ${meeting.client_name || 'N/A'}

ATTENDEES:
  Internal Team  : ${internalNames}
  Client Side    : ${externalNames}

══════════════════════════════════════════════
AGENDA:

${agendaLines}

══════════════════════════════════════════════
DISCUSSION & DECISIONS:

${notesLines}

══════════════════════════════════════════════
ACTION ITEMS:

${actionLines}

NEXT MEETING : ${meeting.next_meeting_date ? fmtDate(meeting.next_meeting_date) : 'TBD'}

══════════════════════════════════════════════
Prepared by  : ${preparedBy}
Date         : ${today}

Krishna Uday & Associates
22-102/160, RK Nagar, Hyderabad – 500047
══════════════════════════════════════════════`;
}

// ── EditableText — module-scope: never remounts on parent re-render ────────────
function EditableText({ field, value, multiline, placeholder, editField, editValue, setEditValue, saveField, setEditField }) {
  if (editField === field) {
    const props = { value: editValue, onChange: e => setEditValue(e.target.value), className: 'input-field text-sm w-full', autoFocus: true };
    return (
      <div className="flex gap-2 items-start">
        {multiline ? <textarea {...props} rows={6} /> : <input {...props} />}
        <div className="flex gap-1 flex-shrink-0">
          <button onClick={() => saveField(field, editValue)} className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600"><Check size={14} /></button>
          <button onClick={() => setEditField(null)} className="p-1.5 bg-gray-200 text-gray-600 rounded hover:bg-gray-300"><X size={14} /></button>
        </div>
      </div>
    );
  }
  return (
    <div className="group flex items-start gap-2 cursor-pointer" onClick={() => { setEditField(field); setEditValue(value || ''); }}>
      <span className={`text-sm flex-1 ${value ? 'text-gray-700' : 'text-gray-400 italic'}`}>{value || placeholder || '— click to edit —'}</span>
      <Edit size={13} className="text-gray-300 group-hover:text-blue-400 mt-0.5 flex-shrink-0" />
    </div>
  );
}

// ── Action Item Row — module-scope ────────────────────────────────────────────
function ActionItemRow({ item, meetingId, staff, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...item });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const save = async () => {
    try {
      await api.put(`/meetings/${meetingId}/action-items/${item.id}`, form);
      toast.success('Updated'); setEditing(false); onRefresh();
    } catch { toast.error('Failed to update'); }
  };

  const del = async () => {
    try {
      await api.delete(`/meetings/${meetingId}/action-items/${item.id}`);
      toast.success('Deleted'); setConfirmDelete(false); onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleDone = async () => {
    const newStatus = item.status === 'Done' ? 'Pending' : 'Done';
    try {
      await api.put(`/meetings/${meetingId}/action-items/${item.id}`, { ...item, status: newStatus });
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  if (editing) {
    return (
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 space-y-3">
        <textarea value={form.action_item} onChange={e => setForm(f => ({ ...f, action_item: e.target.value }))} className="input-field text-sm" rows={2} />
        <div className="grid grid-cols-3 gap-2">
          <select value={form.assigned_to || ''} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} className="input-field text-xs py-1.5">
            <option value="">Assign To</option>{staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="date" value={form.due_date || ''} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="input-field text-xs py-1.5" />
          <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className="input-field text-xs py-1.5">
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="input-field text-xs py-1.5 w-40">
          {ACTION_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="input-field text-xs" rows={2} placeholder="Notes (optional)" />
        <div className="flex gap-2">
          <button onClick={save} className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1"><Save size={12} /> Save</button>
          <button onClick={() => setEditing(false)} className="btn-secondary text-xs py-1.5 px-3">Cancel</button>
        </div>
      </div>
    );
  }

  const isOverdue = item.status !== 'Done' && item.due_date && item.due_date < new Date().toISOString().split('T')[0];
  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border ${item.status === 'Done' ? 'bg-gray-50 border-gray-100' : isOverdue ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'} hover:shadow-sm transition-shadow`}>
      <button onClick={toggleDone} className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${item.status === 'Done' ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-green-400'}`}>
        {item.status === 'Done' && <Check size={12} />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${item.status === 'Done' ? 'line-through text-gray-400' : 'text-gray-800'}`}>{item.action_item}</p>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${PRIORITY_COLOR[item.priority]}`}>{item.priority}</span>
          {item.assignee_name && <span className="text-xs text-gray-500">→ {item.assignee_name}</span>}
          {item.due_date && <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-400'}`}>{isOverdue ? '⚠ ' : ''}Due {fmtDate(item.due_date)}</span>}
          <span className={`text-xs px-1.5 py-0.5 rounded ${item.status === 'Done' ? 'bg-green-100 text-green-700' : item.status === 'In Progress' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>{item.status}</span>
        </div>
        {item.notes && <p className="text-xs text-gray-400 mt-1 italic">{item.notes}</p>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={() => setEditing(true)} className="p-1 text-gray-400 hover:text-blue-500 rounded"><Edit size={14} /></button>
        <button onClick={() => setConfirmDelete(true)} className="p-1 text-gray-400 hover:text-red-500 rounded"><Trash2 size={14} /></button>
      </div>
      {confirmDelete && <ConfirmModal variant="delete" message="Delete this action item?" onConfirm={del} onCancel={() => setConfirmDelete(false)} />}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MeetingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [staff, setStaff] = useState([]);
  const [tab, setTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Inline edit state
  const [editField, setEditField] = useState(null);
  const [editValue, setEditValue] = useState('');

  // New action item
  const [addingAction, setAddingAction] = useState(false);
  const [newAction, setNewAction] = useState({ action_item: '', assigned_to: '', due_date: '', priority: 'Medium', notes: '' });

  // MOM state
  const [momText, setMomText] = useState('');
  const [momCopied, setMomCopied] = useState(false);
  const momRef = useRef(null);

  // Documents state
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docDeleteTarget, setDocDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/meetings/${id}`);
      setMeeting(r.data);
      if (!r.data.mom_text) setMomText(generateMOMText(r.data));
      else setMomText(r.data.mom_text);
    } catch { toast.error('Meeting not found'); navigate('/meetings'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  const loadDocs = useCallback(async () => {
    try { const r = await api.get(`/meetings/${id}/documents`); setDocs(r.data); }
    catch { /* silent */ }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => { api.get('/auth/users').then(r => setStaff(r.data)).catch(() => {}); }, []);

  // Handle hash navigation
  useEffect(() => {
    if (window.location.hash === '#mom') setTab('mom');
    if (window.location.hash === '#documents') setTab('documents');
  }, []);

  const saveField = async (field, value) => {
    setSaving(true);
    try {
      await api.put(`/meetings/${id}`, { ...meeting, [field]: value });
      setMeeting(m => ({ ...m, [field]: value }));
      setEditField(null);
      toast.success('Saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const addActionItem = async () => {
    if (!newAction.action_item.trim()) { toast.error('Action item text required'); return; }
    try {
      await api.post(`/meetings/${id}/action-items`, newAction);
      toast.success('Action item added');
      setNewAction({ action_item: '', assigned_to: '', due_date: '', priority: 'Medium', notes: '' });
      setAddingAction(false);
      load();
    } catch { toast.error('Failed to add'); }
  };

  const saveMOM = async () => {
    setSaving(true);
    try {
      await api.put(`/meetings/${id}`, { ...meeting, mom_text: momText });
      setMeeting(m => ({ ...m, mom_text: momText }));
      toast.success('MOM saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const markMOMSent = async () => {
    try {
      await api.post(`/meetings/${id}/send-mom`);
      setMeeting(m => ({ ...m, mom_sent: 1 }));
      toast.success('MOM marked as sent');
    } catch { toast.error('Failed'); }
  };

  const copyMOM = () => {
    navigator.clipboard.writeText(momText).then(() => {
      setMomCopied(true); setTimeout(() => setMomCopied(false), 2500);
      toast.success('Copied to clipboard');
    });
  };

  const sendMOMWhatsApp = () => {
    if (!meeting?.client_whatsapp) {
      toast.error('No WhatsApp number on file for this client. Add it on the client profile.', { duration: 4000 });
      return;
    }
    const url = buildWaUrl(meeting.client_whatsapp, momText);
    if (url) window.open(url, '_blank');
  };

  const sendMOMEmail = () => {
    if (!meeting?.client_email) {
      toast.error('No email address on file for this client. Add it on the client profile.', { duration: 4000 });
      return;
    }
    const subject = `Minutes of Meeting — ${meeting.title} — ${fmtDate(meeting.meeting_date)}`;
    window.open(`mailto:${meeting.client_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(momText)}`, '_blank');
  };

  const regenerateMOM = () => { setMomText(generateMOMText(meeting)); toast.success('MOM regenerated from meeting data'); };

  // Documents
  const handleFileUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/meetings/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`"${file.name}" uploaded`);
      loadDocs();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDeleteDoc = async () => {
    try {
      await api.delete(`/meetings/${id}/documents/${docDeleteTarget.id}`);
      toast.success('Document deleted');
      setDocDeleteTarget(null);
      loadDocs();
    } catch { toast.error('Delete failed'); }
  };

  const downloadDoc = doc => {
    downloadExport(`/meetings/${id}/documents/${doc.id}/download`, {}, doc.original_name || doc.filename);
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading meeting...</div>;
  if (!meeting) return null;

  const pendingActions = (meeting.action_items || []).filter(a => a.status !== 'Done');
  const isAdmin = ['partner','senior_staff','super_admin','admin'].includes(user?.role);

  // Shared props for EditableText
  const editableProps = { editField, editValue, setEditValue, saveField, setEditField };

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate('/meetings')} className="mt-1 text-gray-400 hover:text-[#1e3a5f]"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl font-bold text-[#1e3a5f] leading-tight">{meeting.title}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {meeting.client_name && <span className="text-sm text-gray-500">{meeting.client_name}</span>}
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[meeting.status] || 'bg-gray-100 text-gray-600'}`}>{meeting.status}</span>
                {meeting.status === 'Completed' && !meeting.mom_sent && (
                  <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full border border-red-200 flex items-center gap-1"><AlertCircle size={11} /> MOM Not Sent</span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => { const msg = `Reminder: Meeting "${meeting.title}" on ${fmtDate(meeting.meeting_date)} at ${fmt12(meeting.meeting_time)}`; if (!meeting.client_whatsapp) { toast.error('No WhatsApp number on file.'); return; } const url = buildWaUrl(meeting.client_whatsapp, msg); if (url) window.open(url, '_blank'); }}
                className="flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg hover:bg-green-100">
                <MessageCircle size={14} /> WhatsApp Reminder
              </button>
              {meeting.client_email && (
                <a href={`mailto:${meeting.client_email}?subject=Meeting: ${meeting.title}`}
                  className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100">
                  <Mail size={14} /> Email
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick info bar */}
      <div className="card flex flex-wrap items-center gap-4 text-sm py-3">
        <div className="flex items-center gap-1.5 text-gray-600"><CalendarDays size={15} />{fmtDate(meeting.meeting_date)}, {fmt12(meeting.meeting_time)}</div>
        {meeting.duration_minutes && <div className="flex items-center gap-1.5 text-gray-600"><Clock size={15} />{meeting.duration_minutes} min</div>}
        <div className="flex items-center gap-1.5 text-gray-600">
          {meeting.mode === 'Video Call' ? <Video size={15} /> : meeting.mode === 'Phone' ? <Phone size={15} /> : <MapPin size={15} />}
          {meeting.mode}{meeting.location && ` · ${meeting.location}`}
        </div>
        {meeting.assigned_to_name && <div className="flex items-center gap-1.5 text-gray-600"><Users size={15} />{meeting.assigned_to_name}</div>}
        {pendingActions.length > 0 && <div className="flex items-center gap-1.5 text-orange-600 font-medium"><ClipboardList size={15} />{pendingActions.length} pending actions</div>}
        {docs.length > 0 && <div className="flex items-center gap-1.5 text-gray-500"><Paperclip size={15} />{docs.length} doc{docs.length !== 1 ? 's' : ''}</div>}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? 'border-[#1e3a5f] text-[#1e3a5f]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <t.icon size={14} />{t.label}
            {t.id === 'actions' && pendingActions.length > 0 && (
              <span className="ml-1 bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{pendingActions.length}</span>
            )}
            {t.id === 'documents' && docs.length > 0 && (
              <span className="ml-1 bg-gray-400 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">{docs.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab: Details ── */}
      {tab === 'details' && (
        <div className="card space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
              {editField === 'status' ? (
                <div className="flex gap-2 mt-1">
                  <select value={editValue} onChange={e => setEditValue(e.target.value)} className="input-field text-sm flex-1">
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                  <button onClick={() => saveField('status', editValue)} className="p-1.5 bg-green-500 text-white rounded"><Check size={14} /></button>
                  <button onClick={() => setEditField(null)} className="p-1.5 bg-gray-200 rounded"><X size={14} /></button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-1 cursor-pointer group" onClick={() => { setEditField('status'); setEditValue(meeting.status); }}>
                  <span className={`text-sm px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[meeting.status] || ''}`}>{meeting.status}</span>
                  <Edit size={13} className="text-gray-300 group-hover:text-blue-400" />
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Next Meeting</label>
              <div className="mt-1">
                {editField === 'next_meeting_date' ? (
                  <div className="flex gap-2">
                    <input type="date" value={editValue} onChange={e => setEditValue(e.target.value)} className="input-field text-sm flex-1" autoFocus />
                    <button onClick={() => saveField('next_meeting_date', editValue)} className="p-1.5 bg-green-500 text-white rounded"><Check size={14} /></button>
                    <button onClick={() => setEditField(null)} className="p-1.5 bg-gray-200 rounded"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 cursor-pointer group" onClick={() => { setEditField('next_meeting_date'); setEditValue(meeting.next_meeting_date || ''); }}>
                    <span className="text-sm text-gray-700">{meeting.next_meeting_date ? fmtDate(meeting.next_meeting_date) : <span className="text-gray-400 italic">Not scheduled</span>}</span>
                    <Edit size={13} className="text-gray-300 group-hover:text-blue-400" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Attendees */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Attendees ({(meeting.attendees || []).length})</label>
            <div className="flex flex-wrap gap-2">
              {(meeting.attendees || []).map((a, i) => (
                <div key={i} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${a.attendee_type === 'Internal' ? 'bg-blue-100 text-blue-700' : 'bg-orange-50 text-orange-700'}`}>
                  <Users size={12} />
                  {a.attendee_type === 'Internal' ? a.user_name : a.external_name}
                  {a.external_mobile && <span className="opacity-70 ml-1">· {a.external_mobile}</span>}
                </div>
              ))}
              {!(meeting.attendees || []).length && <span className="text-sm text-gray-400 italic">No attendees recorded</span>}
            </div>
          </div>

          {/* Action Items summary */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Action Items Summary</label>
            <div className="flex gap-3">
              <div className="text-center bg-gray-50 rounded-lg px-4 py-3">
                <div className="text-2xl font-bold text-gray-800">{(meeting.action_items || []).length}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
              <div className="text-center bg-orange-50 rounded-lg px-4 py-3">
                <div className="text-2xl font-bold text-orange-600">{pendingActions.length}</div>
                <div className="text-xs text-orange-600">Pending</div>
              </div>
              <div className="text-center bg-green-50 rounded-lg px-4 py-3">
                <div className="text-2xl font-bold text-green-600">{(meeting.action_items || []).filter(a => a.status === 'Done').length}</div>
                <div className="text-xs text-green-600">Done</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Agenda & Notes ── */}
      {tab === 'agenda' && (
        <div className="space-y-5">
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Agenda / Discussion Points</h3>
            <EditableText field="agenda" value={meeting.agenda} multiline placeholder="No agenda recorded. Click to add." {...editableProps} />
          </div>
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Meeting Notes / Decisions</h3>
            <EditableText field="notes" value={meeting.notes} multiline placeholder="No notes yet. Click to add decisions, discussions, outcomes." {...editableProps} />
          </div>
        </div>
      )}

      {/* ── Tab: Action Items ── */}
      {tab === 'actions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Action Items ({(meeting.action_items || []).length})</h3>
            <button onClick={() => setAddingAction(a => !a)} className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1"><Plus size={13} /> Add Action</button>
          </div>

          {addingAction && (
            <div className="card bg-blue-50 border border-blue-200 space-y-3">
              <textarea value={newAction.action_item} onChange={e => setNewAction(f => ({ ...f, action_item: e.target.value }))} className="input-field text-sm" rows={2} placeholder="Describe the action item..." autoFocus />
              <div className="grid grid-cols-3 gap-2">
                <select value={newAction.assigned_to} onChange={e => setNewAction(f => ({ ...f, assigned_to: e.target.value }))} className="input-field text-xs py-1.5">
                  <option value="">Assign To</option>{staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <input type="date" value={newAction.due_date} onChange={e => setNewAction(f => ({ ...f, due_date: e.target.value }))} className="input-field text-xs py-1.5" />
                <select value={newAction.priority} onChange={e => setNewAction(f => ({ ...f, priority: e.target.value }))} className="input-field text-xs py-1.5">
                  {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <textarea value={newAction.notes} onChange={e => setNewAction(f => ({ ...f, notes: e.target.value }))} className="input-field text-xs" rows={2} placeholder="Notes (optional)" />
              <div className="flex gap-2">
                <button onClick={addActionItem} className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1"><Plus size={12} /> Add</button>
                <button onClick={() => setAddingAction(false)} className="btn-secondary text-xs py-1.5 px-3">Cancel</button>
              </div>
            </div>
          )}

          {(meeting.action_items || []).length === 0 ? (
            <div className="card text-center py-8 text-gray-400">
              <ClipboardList size={32} className="mx-auto mb-2 text-gray-300" />
              <p>No action items yet.</p>
              <button onClick={() => setAddingAction(true)} className="btn-primary mt-3 text-xs py-1.5 px-4 flex items-center gap-1 mx-auto"><Plus size={12} /> Add First Action</button>
            </div>
          ) : (
            <div className="space-y-2">
              {(meeting.action_items || []).map(a => (
                <ActionItemRow key={a.id} item={a} meetingId={id} staff={staff} onRefresh={load} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: MOM ── */}
      {tab === 'mom' && (
        <div className="space-y-4">
          {/* Status + action bar */}
          <div className="card space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-700">Minutes of Meeting (MOM)</h3>
                {meeting.mom_sent ? (
                  <span className="text-xs text-green-600 flex items-center gap-1 mt-0.5">
                    <CheckSquare size={12} /> MOM sent{meeting.mom_sent_at ? ` on ${fmtDate(meeting.mom_sent_at?.split('T')[0])}` : ''}
                  </span>
                ) : (
                  <span className="text-xs text-orange-600 flex items-center gap-1 mt-0.5">
                    <AlertCircle size={12} /> MOM not yet sent to client
                  </span>
                )}
              </div>
              {/* Generate / Regenerate */}
              <button onClick={regenerateMOM}
                className="flex items-center gap-2 bg-[#1e3a5f] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#264873] transition-colors">
                <RefreshCw size={15} />{meeting.mom_text ? 'Regenerate MOM' : 'Generate MOM'}
              </button>
            </div>

            {/* Distribution buttons */}
            <div className="flex flex-wrap gap-2 pt-1 border-t border-gray-100">
              <button onClick={saveMOM} disabled={saving}
                className="flex items-center gap-1.5 text-xs bg-gray-800 text-white px-3 py-1.5 rounded-lg hover:bg-gray-900 transition-colors">
                <Save size={13} />{saving ? 'Saving…' : 'Save MOM'}
              </button>
              <button onClick={copyMOM}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${momCopied ? 'bg-green-50 text-green-700 border-green-300' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                {momCopied ? <Check size={13} /> : <Copy size={13} />}{momCopied ? 'Copied!' : 'Copy'}
              </button>
              <button onClick={sendMOMWhatsApp}
                className="flex items-center gap-1.5 text-xs bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-600 transition-colors">
                <MessageCircle size={13} /> WhatsApp
              </button>
              <button onClick={sendMOMEmail}
                className="flex items-center gap-1.5 text-xs bg-blue-500 text-white px-3 py-1.5 rounded-lg hover:bg-blue-600 transition-colors">
                <Mail size={13} /> Email
              </button>
              {!meeting.mom_sent && (
                <button onClick={markMOMSent}
                  className="flex items-center gap-1.5 text-xs bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 transition-colors">
                  <CheckSquare size={13} /> Mark as Sent
                </button>
              )}
            </div>
          </div>

          {/* Editable MOM text */}
          <div className="card p-0 overflow-hidden">
            <textarea
              ref={momRef}
              value={momText}
              onChange={e => setMomText(e.target.value)}
              className="w-full font-mono text-xs p-5 h-[600px] resize-y focus:outline-none border-0 leading-relaxed bg-gray-50"
              spellCheck={false}
              placeholder="Click 'Generate MOM' to auto-fill from meeting data, or type manually..."
            />
          </div>
          <p className="text-xs text-gray-400">Auto-generated from meeting data. Edit freely, then Save. Use WhatsApp or Email to distribute.</p>
        </div>
      )}

      {/* ── Tab: Documents ── */}
      {tab === 'documents' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Meeting Documents ({docs.length})</h3>
            <div>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.zip,.txt,.ppt,.pptx" />
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="btn-primary flex items-center gap-2 text-sm py-2">
                <Upload size={15} />{uploading ? 'Uploading…' : 'Upload Document'}
              </button>
            </div>
          </div>

          {/* Drop zone */}
          <div
            className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-[#1e3a5f] hover:bg-blue-50/30 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { const dt = new DataTransfer(); dt.items.add(f); if (fileInputRef.current) { fileInputRef.current.files = dt.files; handleFileUpload({ target: fileInputRef.current }); } } }}>
            <Paperclip size={24} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-gray-400">Click or drag & drop to upload</p>
            <p className="text-xs text-gray-300 mt-1">PDF, Word, Excel, Images, ZIP — max 20 MB</p>
          </div>

          {/* Document list */}
          {docs.length === 0 ? (
            <div className="card text-center py-8 text-gray-400">
              <Paperclip size={32} className="mx-auto mb-2 text-gray-300" />
              <p>No documents uploaded yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map(doc => (
                <div key={doc.id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 hover:shadow-sm transition-shadow">
                  <span className="text-xl flex-shrink-0">{fileIcon(doc.original_name)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{doc.original_name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {fmtBytes(doc.file_size)}
                      {doc.uploader_name && ` · Uploaded by ${doc.uploader_name}`}
                      {doc.uploaded_at && ` · ${fmtDate(doc.uploaded_at?.split('T')[0] || doc.uploaded_at?.split(' ')[0])}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => downloadDoc(doc)}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 px-2 py-1.5 rounded-lg hover:bg-blue-50 transition-colors">
                      <FileDown size={14} /> Download
                    </button>
                    {(isAdmin || doc.uploaded_by === user?.id) && (
                      <button onClick={() => setDocDeleteTarget(doc)}
                        className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Confirm delete document */}
      {docDeleteTarget && (
        <ConfirmModal
          variant="delete"
          message={`Delete "${docDeleteTarget.original_name}"? This cannot be undone.`}
          onConfirm={handleDeleteDoc}
          onCancel={() => setDocDeleteTarget(null)}
        />
      )}
    </div>
  );
}
